import {
  EnglishTestNotReqdReason,
  EnglishTestType,
  GradeLevel,
  IQualification,
  IStudy,
  ApplicantOfferRequirementTask,
  IEnglishTest,
  IApplicantOffer,
} from 'types';
import { getFulfilmentProp, IEnglishTestData } from '@/utilities/fulfilmentData';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';

/**
 * Get English test data from offer requirement fulfilmentData
 */
export function getEnglishTestDataFromFulfilment(offer: IApplicantOffer | undefined): IEnglishTestData | null {
  if (!offer?.requirements) return null;

  // Extract all tasks from hierarchical requirements
  const allTasks = getAllTasksFromRequirements(offer.requirements);
  const englishLanguageTask = allTasks.find(task => task.type === ApplicantOfferRequirementTask.EnglishLanguageResults);

  if (!englishLanguageTask) return null;

  const { pendingChanges } = englishLanguageTask;
  const fulfilmentData = pendingChanges?.fulfilmentData ?? englishLanguageTask.fulfilmentData;

  if (!fulfilmentData) return null;

  return getFulfilmentProp(fulfilmentData, 'englishTest') as IEnglishTestData | null;
}

/**
 * Merge fulfilmentData with application English test data, prioritizing fulfilmentData
 * IEnglishTest is a discriminated union, so we need to construct the correct variant
 */
export function mergeEnglishTestData(
  appEnglishTest: IEnglishTest,
  fulfilmentData: IEnglishTestData | null,
): IEnglishTest {
  if (!fulfilmentData || fulfilmentData.hasTakenTest === undefined) {
    return appEnglishTest;
  }

  // If fulfilmentData indicates test was taken, construct the "taken" variant
  if (fulfilmentData.hasTakenTest === true) {
    const appTest = appEnglishTest.hasTakenTest ? appEnglishTest : null;
    return {
      hasTakenTest: true as const,
      type: (fulfilmentData.type ?? appTest?.type ?? EnglishTestType.Other) as EnglishTestType,
      referenceNo: fulfilmentData.referenceNo ?? appTest?.referenceNo ?? '',
      score: fulfilmentData.score ?? appTest?.score ?? '',
      date: fulfilmentData.date ?? appTest?.date ?? '',
      ...(fulfilmentData.typeOther && { typeOther: fulfilmentData.typeOther }),
      ...(fulfilmentData.validUntil || appTest?.validUntil
        ? { validUntil: fulfilmentData.validUntil ?? appTest?.validUntil }
        : {}),
    };
  }

  // If fulfilmentData indicates test was not taken, construct the "not taken" variant
  return {
    hasTakenTest: false as const,
    reasonNotReqd: (fulfilmentData.reasonNotReqd ??
      (appEnglishTest.hasTakenTest === false
        ? appEnglishTest.reasonNotReqd
        : EnglishTestNotReqdReason.Other)) as EnglishTestNotReqdReason,
    ...(fulfilmentData.reasonNotReqdOther && {
      reasonNotReqdOther: fulfilmentData.reasonNotReqdOther,
    }),
  };
}

/**
 * Get merged reason not required, prioritizing fulfilmentData
 */
export function getMergedReasonNotReqd(
  appReason: string | undefined,
  fulfilmentData: IEnglishTestData | null,
): string | undefined {
  if (!fulfilmentData) return appReason;
  if (fulfilmentData.hasTakenTest === false) {
    return fulfilmentData.reasonNotReqd ?? appReason;
  }
  return appReason;
}

/**
 * Get merged reason not required other, prioritizing fulfilmentData
 */
export function getMergedReasonNotReqdOther(
  appReason: string | undefined,
  fulfilmentData: IEnglishTestData | null,
): string | undefined {
  if (!fulfilmentData) return appReason;
  if (fulfilmentData.hasTakenTest === false) {
    return fulfilmentData.reasonNotReqdOther ?? appReason;
  }
  return appReason;
}

/**
 * Get qualification label for display
 */
export function getQualificationLabel(qualification: IQualification, education: IStudy): string {
  // Prefer new YHS fields (qualificationTypeLabel) over old fields (level)
  const levelLabel =
    qualification?.qualificationTypeLabel ||
    qualification?.otherQualificationName ||
    qualification?.levelLabel ||
    GradeLevel?.[qualification?.level as keyof typeof GradeLevel] ||
    qualification?.level;

  const institute = education?.institutionName ?? 'Unknown institute';
  const subject = qualification?.subject ?? 'Unknown subject';
  const level = levelLabel ?? 'Unknown level';

  return `${level} in ${subject} from ${institute}`;
}

/**
 * Get formatted reason not required text
 */
export function getNotRequiredReason(
  reasonNotReqd: string | undefined,
  isPrevQualification: boolean,
  qualification: { label: string; value: string } | undefined,
  hasEnglishTest: boolean,
): string | undefined {
  if (hasEnglishTest) return undefined;
  const notRequiredReason = isPrevQualification
    ? `${reasonNotReqd}, ${qualification?.label ?? 'Unknown qualification.'}`
    : reasonNotReqd;

  return notRequiredReason;
}

/**
 * Check if reason not required is "Other"
 */
export function hasReasonNotRequiredOther(
  reasonNotReqd: string | undefined,
  initialReasonNotReqd: string | undefined,
): boolean {
  if (reasonNotReqd && reasonNotReqd === EnglishTestNotReqdReason.Other) return true;
  if (initialReasonNotReqd && initialReasonNotReqd === EnglishTestNotReqdReason.Other) return true;
  return false;
}

/**
 * Check if reason not required exists and is not "Other"
 * Returns true only if the current reason exists and is not "Other"
 */
export function hasReasonNotRequired(
  reasonNotReqd: string | undefined,
  initialReasonNotReqd: string | undefined,
): boolean {
  // Check current reason first - if it exists and is not Other, return true
  if (reasonNotReqd && reasonNotReqd !== EnglishTestNotReqdReason.Other) return true;
  // If current is Other or undefined, check initial - but only if current is undefined
  // If current is Other, we should show the "Other" field instead
  if (!reasonNotReqd && initialReasonNotReqd && initialReasonNotReqd !== EnglishTestNotReqdReason.Other) {
    return true;
  }
  return false;
}
